import "@/telemetry/clientLogger";
import "bootstrap";
// Tailwind CSS v4 — utilities only (sem preflight, compatível com Bootstrap)
import "../css/utilities.css";
import { createApp, type Directive } from "vue";
import { getBrowserId } from "@/telemetry/browserId";
import { setLastCorrelationId, setLastErrorCorrelationId } from "@/telemetry/correlation";
import { router } from "@/router/routes.js";
import { SweetModal } from "sweet-modal-vue-3";
import App from "@/App.vue";
import axios from "axios";
import BootstrapVue, { BFormCheckbox, BFormRadio, BFormSelect } from "bootstrap-vue";
import Breadcrumb from "@/components/comum/toolbar/Breadcrumb.vue";
import canDirective from "@/directives/can.js";
import checkForUpdates from "@/checkForUpdates.js";
import componentTombstone from "@/telemetry/componentTombstone";
import Cookies from "js-cookie";
import DebugCode from "@/components/DebugCode.vue";
import DialogModal from "@/components/comum/DialogModal.vue";
import focusDirective from "@/directives/focus.js";
import globalComputed from "@/globals/computed";
import globalMethods from "@/globals/methods";
import jQuery from "jquery";
import LayoutButton from "@/components/comum/buttons/LayoutButton.vue";
import LayoutToolbar from "@/components/comum/layout/toolbar/LayoutToolbar.vue";
import LoadingWrapper from "@/components/comum/animations/loading-wrapper.vue";
import modelCompat from "@/directives/modelCompat";
import moment from "moment";
import Notifications from "@kyvg/vue3-notification";
import numeral from "numeral";
import Popper from "popper.js";
import ScaffoldSubmenu from "./layout/ScaffoldSubmenu.vue";
import SeparatorDot from "@/components/comum/separators/separator-dot.vue";
import VueNumberAnimation from "vue-number-animation";

// --------------------------- GLOBAL PROPERTIES ---------------------------
window.$ = window.jQuery = jQuery;
window.axios = axios;
window.Cookies = Cookies;
window.Popper = Popper;

// ------------------------------ AXIOS CONFIG ------------------------------
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
axios.defaults.headers.common["X-Browser-ID"] = getBrowserId();

// CSRF Token
let token = document.head.querySelector<HTMLMetaElement>("meta[name=\"csrf-token\"]");
if (token) {
  axios.defaults.headers.common["X-CSRF-TOKEN"] = token.content;
} else {
  console.error("CSRF token not found: https://laravel.com/docs/csrf#csrf-x-csrf-token");
}

// Auth Bearer Token
let bearer = Cookies.get("access_token");
if (bearer) {
  axios.defaults.headers.common["Authorization"] = bearer;
}

// ------------------------------- AXIOS INTERCEPTORS -------------------------------
// Interceptor para tratar erros 401 (não autorizado)
axios.interceptors.response.use(
  (response) => {
    setLastCorrelationId(response.headers?.["x-correlation-id"]);
    return response;
  },
  (error) => {
    setLastErrorCorrelationId(error.response?.headers?.["x-correlation-id"]);
    if (error.response && error.response.status === 401) {
      Cookies.remove("access_token");
      Cookies.remove("emitente_id");
      delete axios.defaults.headers.common["Authorization"];
      window.location.reload();
    }
    return Promise.reject(error);
  },
);

// ------------------------------- LOCALE CONFIG -------------------------------
numeral.locale("br");
numeral.register("locale", "br", { currency: { symbol: "R$" }, delimiters: { decimal: ",", thousands: "." } });
moment.locale("pt-br");

// ------------------------------- VUE APP SETUP -------------------------------
const app = createApp(App);

// Forward Vue component errors to telemetry (console.error is captured by the
// client logger) while keeping them visible in the dev console.
app.config.errorHandler = (err, _instance, info) => {
  console.error(`[Vue] ${info}`, err);
};

// Usage of mixins is deprecated.
app.mixin({ computed: globalComputed, methods: globalMethods });

// --------------------------------- VUE.USE --------------------------------
app.use(router);
app.use(BootstrapVue);
for (const component of [BFormSelect, BFormCheckbox, BFormRadio]) {
  const options = (component as unknown as { options: { directives?: Record<string, Directive> } }).options;
  options.directives = { ...options.directives, model: modelCompat };
}
app.use(Notifications);
app.component("NumberAnimation", VueNumberAnimation);
app.use(componentTombstone);

// ------------------------------ VUE.COMPONENT -----------------------------
app.component("Breadcrumb", Breadcrumb);
app.component("DebugCode", DebugCode);
app.component("DialogModal", DialogModal);
app.component("LayoutButton", LayoutButton);
app.component("LayoutToolbar", LayoutToolbar);
app.component("LoadingWrapper", LoadingWrapper);
app.component("ScaffoldSubmenu", ScaffoldSubmenu);
app.component("SeparatorDot", SeparatorDot);
app.component("SweetModal", SweetModal);

// ------------------------------- VUE DIRECTIVES --------------------------
app.directive("can", canDirective);
app.directive("focus", focusDirective);

// ------------------------------- VUE APP ---------------------------------
app.mount("#app");

checkForUpdates();
